/**
 * Date logic for the Black Friday backwards planner.
 *
 * Given a target sale date and an inventory lead time, we work backwards to a
 * dated list of prep milestones. The inventory-order deadline is the anchor
 * (the "hero"): stock is assumed to land a buffer before the sale, and the
 * order must be placed a full lead time before that.
 *
 * All dates are handled in local time to match the user's own calendar.
 */

const DAY = 24 * 60 * 60 * 1000;

/** Stock is assumed to land this many days before the sale. */
const LANDING_BUFFER_DAYS = 14;

export interface Milestone {
  name: string;
  desc: string;
  date: Date;
  /** The inventory-order deadline - the milestone the whole plan hinges on. */
  hero?: boolean;
  /** The sale itself - rendered as a divider rather than a to-do. */
  marker?: boolean;
  /** Stable id for milestones the UI references directly (cash note, etc.). */
  key?: "funding" | "deposit" | "balance";
}

/** Midnight today, in local time. */
export function today(): Date {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

/** The next Black Friday (day after the fourth Thursday of November). */
export function nextBlackFriday(): Date {
  for (let year = today().getFullYear(); ; year++) {
    const nov1 = new Date(year, 10, 1);
    const firstThursdayOffset = (4 - nov1.getDay() + 7) % 7; // 4 = Thursday
    const fourthThursday = new Date(year, 10, 1 + firstThursdayOffset + 21);
    const bf = new Date(fourthThursday.getTime() + DAY);
    if (bf.getTime() >= today().getTime()) return bf;
  }
}

function minusDays(date: Date, days: number): Date {
  return new Date(date.getTime() - days * DAY);
}

/** Deadlines that land on a weekend move back to the Friday. */
function snapOffWeekend(date: Date): Date {
  const d = new Date(date);
  if (d.getDay() === 6) return minusDays(d, 1); // Saturday
  if (d.getDay() === 0) return minusDays(d, 2); // Sunday
  return d;
}

export function fmt(date: Date): string {
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

export function fmtLong(date: Date): string {
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/** Format a Date as the yyyy-mm-dd value a native date input expects. */
export function toDateValue(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}

export function weeksBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / (7 * DAY));
}

/** Build the dated, chronologically-sorted milestone plan. */
export function buildPlan(bfDate: Date, leadDays: number): Milestone[] {
  const landing = snapOffWeekend(minusDays(bfDate, LANDING_BUFFER_DAYS));
  const orderDeadline = snapOffWeekend(minusDays(landing, leadDays));
  // Suppliers typically invoice the balance when the order ships, roughly the
  // final third of the lead time before stock lands.
  const balanceDue = snapOffWeekend(
    minusDays(landing, Math.round(leadDays / 3))
  );
  const fundingDecision = snapOffWeekend(minusDays(orderDeadline, 14));

  const plan: Milestone[] = [
    {
      name: "Lock your offer and margin maths",
      desc: "Review last year's numbers, then set discount depth, bundles and your margin floor for every SKU.",
      date: snapOffWeekend(minusDays(bfDate, 77)),
    },
    {
      name: "Decide how you're funding your stock order",
      desc: "Cash, credit or financing - the money needs to be in your account before the deposit is due, and approvals take time.",
      date: fundingDecision,
      key: "funding",
    },
    {
      name: "Place your order and pay the deposit",
      desc: "Commit with your supplier so stock lands with room to spare. Most suppliers take a deposit (often ~30%) on order.",
      date: orderDeadline,
      hero: true,
      key: "deposit",
    },
    {
      name: "Brief your creative",
      desc: "Ads, landing pages and email assets - brief now so everything is produced and approved weeks before launch, not days.",
      date: snapOffWeekend(minusDays(bfDate, 63)),
    },
    {
      name: "Clean your email list and step up your sending",
      desc: "Re-engage or drop lapsed subscribers and increase your cadence gradually, so your sender reputation holds when volume spikes in November.",
      date: snapOffWeekend(minusDays(bfDate, 56)),
    },
    {
      name: "Start your ad warm-up",
      desc: "Run prospecting now so your audiences are built before auction costs spike.",
      date: snapOffWeekend(minusDays(bfDate, 42)),
    },
    {
      name: "Balance payment due as your order ships",
      desc: "The remaining ~70% is typically invoiced when goods leave the supplier - before you've sold a single unit.",
      date: balanceDue,
      key: "balance",
    },
    {
      name: "Segment your list and schedule your sequences",
      desc: "VIPs, last year's BFCM buyers, lapsed customers - with early access, launch, reminder and last-chance emails queued, not written on the fly.",
      date: snapOffWeekend(minusDays(bfDate, 21)),
    },
    {
      name: "Stock lands at your warehouse or 3PL",
      desc: "Counted, synced across channels, and ready to pick.",
      date: landing,
    },
    {
      name: "Code freeze and final checks",
      desc: "No site changes from here. Load test done, offers tested end to end, team briefed.",
      date: snapOffWeekend(minusDays(bfDate, 7)),
    },
    {
      name: "Black Friday weekend",
      desc: "Your sale is live. Watch sell-through daily - what sells out first tells you what to reorder.",
      date: bfDate,
      marker: true,
    },
    {
      name: "Restock winners for the Christmas run",
      desc: "A fast top-up order on your best sellers can capture December demand while the weekend's cash is still landing. How much this applies depends on your category: gifting-led brands keep selling into late December, while for others the peak ends here.",
      date: minusDays(bfDate, -4),
    },
  ];

  plan.sort((a, b) => a.date.getTime() - b.date.getTime());
  return plan;
}

const pad = (n: number) => String(n).padStart(2, "0");

const icsDateStamp = (d: Date) =>
  `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;

// RFC 5545 §3.8.7.2: DTSTAMP must be a UTC date-time in YYYYMMDDTHHMMSSZ form.
const icsUtcStamp = (d: Date) =>
  `${d.toISOString().replace(/[-:]|\.\d{3}/g, "")}`;

// RFC 5545 §3.3.11: TEXT values must escape backslash, comma and semicolon,
// and encode line breaks as the literal two-character sequence "\n".
const escapeIcsText = (s: string) =>
  s
    .replace(/\\/g, "\\\\")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;")
    .replace(/\r?\n/g, "\\n");

/** Build an iCalendar (.ics) document with one all-day event per milestone. */
export function buildIcs(plan: Milestone[]): string {
  const now = new Date();
  const stampNow = icsUtcStamp(now);

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Wayflyer//BFCM Backwards Planner//EN",
  ];
  plan.forEach((m, i) => {
    const start = icsDateStamp(m.date);
    const end = icsDateStamp(new Date(m.date.getTime() + DAY));
    lines.push(
      "BEGIN:VEVENT",
      `UID:bfcm-planner-${i}-${start}@wayflyer.com`,
      `DTSTAMP:${stampNow}`,
      `DTSTART;VALUE=DATE:${start}`,
      `DTEND;VALUE=DATE:${end}`,
      `SUMMARY:${escapeIcsText(`BFCM: ${m.name}`)}`,
      `DESCRIPTION:${escapeIcsText(`${m.desc} (From the Wayflyer Black Friday toolkit)`)}`,
      "END:VEVENT"
    );
  });
  lines.push("END:VCALENDAR");
  return `${lines.join("\r\n")}\r\n`;
}
